<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

// Shared components
import DatePicker from '@/components/Applications/Shared/DatePicker.vue'
import FileUpload from '../../Shared/FileUpload.vue'
import SSNInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = withDefaults(defineProps<{
  disable?: boolean
  // allow override if your backend expects specific constants
  umsoknType?: number
  fileSpace?: number
}>(), {
  disable: false,
  // old flow used $getConst('UtgreidslaSereign'), here default to 4 (Utgreidsla Séreign)
  umsoknType: 4,
  fileSpace: 4,
})

/* ---- Validation ---- */
const rules = {
  umsokn: {
    latnaName: { required },
    latnaKennitala: { required },
    danarDagur: { required }, // ISO "YYYY-MM-DD" from DatePicker
  },
}
const v$ = useVuelidate(rules, { umsokn })

/* ---- Utilities ---- */
function digitsOnly(v: string) {
  return (v || '').replace(/\D/g, '')
}

/* ---- Expose validate() for ApplicationBase ---- */
defineExpose({
  async validate(): Promise<boolean> {
    await v$.value.$validate()
    return !v$.value.$error
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <v-row>
        <v-col>
          <div class="forms__header">
            {{ t('utgreidsla_umsokn_upplysingar_latna_header1') }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <!-- Nafn hins látna -->
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.latnaName"
            :label="t('utgreidsla_umsokn_upplysingar_latna_nafn')"
            variant="outlined"
            density="comfortable"
            :readonly="props.disable"
            :error="v$.umsokn.latnaName.$error"
            :error-messages="v$.umsokn.latnaName.$error ? [t('utgreidsla_umsokn_upplysingar_latna_nafn_error')] : []"
          />
        </v-col>

        <!-- Kennitala hins látna -->
        <v-col cols="12" md="6">
          <SSNInput
            v-model="umsokn.latnaKennitala"
            :label="t('utgreidsla_umsokn_upplysingar_latna_kennitala')"
            variant="outlined"
            :readonly="props.disable"
            type="tel"
            inputmode="numeric"
            @update:model-value="v => (umsokn.latnaKennitala = digitsOnly(v))"
            :error="v$.umsokn.latnaKennitala.$error"
            :error-messages="v$.umsokn.latnaKennitala.$error ? [t('utgreidsla_umsokn_upplysingar_latna_kennitala_error')] : []"
          />
        </v-col>
      </v-row>

      <v-row>
        <!-- Dánardagur -->
        <v-col cols="12" md="6">
          <DatePicker
            v-model="umsokn.danarDagur"
            :disabled="props.disable"
            :label="t('utgreidsla_umsokn_upplysingar_latna_dags')"
            :error="v$.umsokn.danarDagur.$error"
            :error-messages="v$.umsokn.danarDagur.$error ? [t('utgreidsla_umsokn_upplysingar_latna_dags_error')] : []"
          />
        </v-col>
      </v-row>

      <!-- Skjöl (dánarvottorð o.fl.) -->
      <v-row class="mt-2">
        <v-col cols="12">
          <p class="forms__header" style="font-size:18px">
            {{ t('utgreidsla_umsokn_upplysingar_latna_file_header') }}
          </p>

          <FileUpload
            :umsoknType="props.umsoknType"
            :umsoknId="umsokn.id"
            :filespace="props.fileSpace"
            :disable="props.disable"
          />
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
</style>
